import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function FollowUpTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState('');
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/rcm/follow-up/queue'); setData(r.data); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (item) => {
    try {
      await api.post(`/rcm/follow-up/${item.source_object_id}/acknowledge`, {
        source_type: item.source_object_type, notes: 'Acknowledged from workspace',
      });
      load();
    } catch(e) { console.error(e); }
  };

  const resolve = async (item) => {
    if (!resolution) { alert('Resolution reason is required'); return; }
    try {
      await api.post(`/rcm/follow-up/${item.source_object_id}/resolve`, {
        source_type: item.source_object_type, resolution, notes,
      });
      setSelected(null); setResolution(''); setNotes('');
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (loading && !data) return <div className="p-4 text-xs text-muted-foreground">Loading follow-up queue...</div>;

  const summary = data?.summary || {};
  const items = data?.items || [];
  const thresholds = data?.thresholds_used || {};
  const filtered = filter === 'all' ? items : items.filter(i => i.follow_up_type === filter);

  // Type labels and icons for clarity
  const typeConfig = {
    not_submitted: { label: 'Not Submitted', color: 'text-amber-600', bg: 'bg-amber-50' },
    submitted_no_response: { label: 'No Response', color: 'text-orange-600', bg: 'bg-orange-50' },
    accepted_not_paid: { label: 'Unpaid', color: 'text-red-600', bg: 'bg-red-50' },
    denied_open: { label: 'Denial Open', color: 'text-red-600', bg: 'bg-red-50' },
    rejected_open: { label: 'Rejection Open', color: 'text-amber-600', bg: 'bg-amber-50' },
    remittance_unmatched: { label: 'Unmatched', color: 'text-purple-600', bg: 'bg-purple-50' },
    posting_exception: { label: 'Posting Exception', color: 'text-red-600', bg: 'bg-red-50' },
    rebill_ready: { label: 'Rebill Ready', color: 'text-sky-600', bg: 'bg-sky-50' },
    appeal_ready: { label: 'Appeal Ready', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  };

  return (
    <div className="space-y-4" data-testid="followup-tab">
      {/* Summary chips — filter by clicking */}
      <div className="flex flex-wrap gap-2" data-testid="followup-filters">
        <button onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-muted/50'}`}>
          All ({items.length})
        </button>
        {Object.entries(summary).filter(([_,v]) => v > 0).map(([type, count]) => {
          const cfg = typeConfig[type] || { label: type, color: '', bg: '' };
          return (
            <button key={type} onClick={() => setFilter(type)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === type ? 'bg-primary text-primary-foreground border-primary' : `${cfg.bg} ${cfg.color} border-border hover:opacity-80`}`}>
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Queue */}
      <Card>
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs">Follow-Up Items ({filtered.length})</CardTitle>
          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {filtered.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No follow-up items</p> : (
          <div className="space-y-1.5" data-testid="followup-list">
            {filtered.map((item, idx) => {
              const cfg = typeConfig[item.follow_up_type] || { label: item.follow_up_type, color: '', bg: 'bg-muted' };
              const isOpen = selected?.source_object_id === item.source_object_id;
              return (
                <div key={idx} className={`border rounded-lg overflow-hidden ${isOpen ? 'ring-1 ring-primary' : ''}`}>
                  <div className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30" onClick={() => setSelected(isOpen ? null : item)}>
                    {/* Type badge */}
                    <span className={`${cfg.bg} ${cfg.color} px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap`}>{cfg.label}</span>
                    {/* Source object */}
                    <span className="text-[10px] text-muted-foreground font-mono w-20 shrink-0">{item.source_object_type}</span>
                    {/* Payer */}
                    <span className="text-xs w-32 truncate">{item.payer_name || item.payer_id || '—'}</span>
                    {/* Amount */}
                    {item.amount > 0 && <span className="text-xs font-medium w-20 text-right">{fmt(item.amount)}</span>}
                    {/* Age */}
                    {item.age_days > 0 && (
                      <Badge variant={item.age_days > 90 ? 'destructive' : item.age_days > 30 ? 'default' : 'secondary'} className="text-[10px] shrink-0">{item.age_days}d</Badge>
                    )}
                    {/* Reason — the most important field */}
                    <span className="text-xs text-muted-foreground flex-1 truncate">{item.reason_for_creation}</span>
                    <ChevronRight className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </div>

                  {/* Expanded detail — inline, not a separate panel */}
                  {isOpen && (
                    <div className="border-t px-3 py-3 bg-muted/10 space-y-3">
                      {/* Source info */}
                      <Card className="border-slate-200 bg-slate-50/50">
                        <CardContent className="p-2.5 text-xs space-y-1">
                          <p className="font-semibold text-slate-600">Source Context</p>
                          <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="capitalize">{item.follow_up_type.replace(/_/g, ' ')}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Source object</span><span className="font-mono text-[10px]">{item.source_object_type}: {item.source_object_id?.slice(0,12)}...</span></div>
                          {item.claim_id && <div className="flex justify-between"><span className="text-muted-foreground">Linked claim</span><span className="font-mono text-[10px]">{item.claim_id?.slice(0,12)}...</span></div>}
                          <div className="flex justify-between"><span className="text-muted-foreground">Reason</span><span className="text-right max-w-[300px]">{item.reason_for_creation}</span></div>
                          {item.age_days > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Age</span><span>{item.age_days} days (threshold: {thresholds[item.follow_up_type.replace('_open', '') + '_days'] || '—'}d)</span></div>}
                        </CardContent>
                      </Card>

                      {/* Actions — acknowledge / resolve */}
                      <div className="space-y-2">
                        <p className="text-[10px] text-muted-foreground uppercase font-medium">Actions (does not modify financial data)</p>
                        <div className="flex gap-2">
                          <Button size="sm" className="h-7 text-xs" variant="outline" onClick={() => acknowledge(item)} data-testid="action-ack">
                            <Eye className="h-3 w-3 mr-1" />Acknowledge
                          </Button>
                        </div>
                        <div className="space-y-1.5">
                          <input type="text" className="w-full text-xs border rounded px-2 py-1.5" placeholder="Resolution reason (required)..."
                            value={resolution} onChange={e => setResolution(e.target.value)} data-testid="resolve-reason" />
                          <input type="text" className="w-full text-xs border rounded px-2 py-1.5" placeholder="Notes (optional)..."
                            value={notes} onChange={e => setNotes(e.target.value)} data-testid="resolve-notes" />
                          <Button size="sm" className="h-7 text-xs" onClick={() => resolve(item)} data-testid="action-resolve">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Resolve
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>)}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════
// ASSIGNMENTS TAB (Phase 5)
// ═══════════════════════════════════════

export { FollowUpTab };
